import React from 'react';
import { API } from '../api';
import { Field, Section, Toggle } from './ui';

/*
 * The subscription levels themselves: what they are called, what they cost
 * and what they entitle a club to.
 *
 * These used to be a list in code, so changing a price was a deployment.
 * Prices are a commercial decision and commercial decisions move faster than
 * releases, so they are rows now.
 *
 * Every limit box may be left empty, and empty means unlimited rather than
 * zero. Zero is a real answer — the free plan generates no posters at all —
 * so the two cannot look the same.
 */

const BLANK = {
  label: '', blurb: '', priceCents: 0,
  generationsPerMonth: 0, postersKept: 5, photosKept: 10,
  maxMembers: '', maxAdmins: '', clubSiteIncluded: false,
};

/* Money is held in cents and typed in dollars. Doing that conversion in one
   place stops a price ever being out by a factor of a hundred. */
const toDollars = cents => (cents ? (cents / 100).toFixed(2).replace(/\.00$/, '') : '');
const toCents = dollars => Math.round(Number(dollars || 0) * 100);

function LimitInput({ label, value, onChange, help }) {
  return (
    <Field label={label} help={help}>
      <input className="w3-input plan-num" type="number" min="0"
        aria-label={label}
        placeholder="unlimited"
        value={value === null || value === undefined ? '' : value}
        onChange={e => onChange(e.target.value === '' ? null : e.target.value)} />
    </Field>
  );
}

function PlanRow({ plan, onSave, onDelete, showToast }) {
  const [draft, setDraft] = React.useState(plan);
  const [dirty, setDirty] = React.useState(false);

  React.useEffect(() => { setDraft(plan); setDirty(false); }, [plan]);

  const set = patch => { setDraft(d => ({ ...d, ...patch })); setDirty(true); };

  return (
    <div className="plan-row">
      <div className="plan-head">
        <input className="w3-input plan-label" aria-label="Plan name"
          value={draft.label} onChange={e => set({ label: e.target.value })} />
        <div className="plan-price">
          <span className="plan-currency">$</span>
          <input className="w3-input" type="number" min="0" step="0.01"
            aria-label={`Monthly price for ${plan.label}`}
            placeholder="0"
            value={toDollars(draft.priceCents)}
            onChange={e => set({ priceCents: toCents(e.target.value) })} />
          <span className="plan-per">/month</span>
        </div>
        <span className="plan-count">
          {plan.clubCount} club{plan.clubCount === 1 ? '' : 's'}
        </span>
      </div>

      <input className="w3-input plan-blurb" aria-label={`What ${plan.label} is for`}
        placeholder="What this plan is for"
        value={draft.blurb || ''} onChange={e => set({ blurb: e.target.value })} />

      <div className="gs-field-grid" style={{ '--gs-cols': 'repeat(auto-fit, minmax(150px, 1fr))' }}>
        <LimitInput label="Generations a month" value={draft.generationsPerMonth}
          help="0 means no poster generation." onChange={v => set({ generationsPerMonth: v })} />
        <LimitInput label="Posters kept" value={draft.postersKept}
          onChange={v => set({ postersKept: v })} />
        <LimitInput label="Photos kept" value={draft.photosKept}
          onChange={v => set({ photosKept: v })} />
        <LimitInput label="Members" value={draft.maxMembers}
          help="Blank for unlimited." onChange={v => set({ maxMembers: v })} />
        <LimitInput label="Admin accounts" value={draft.maxAdmins}
          help="Blank for unlimited." onChange={v => set({ maxAdmins: v })} />
      </div>

      {/* Not a LimitInput: every box above is an allowance being counted
          against, and this is a yes or a no. A club either has a website or
          it does not — there is nothing to meter. */}
      <label className="poster-switch" style={{ marginTop: 'var(--gs-space-3)' }}>
        <Toggle ariaLabel={`Club website included in ${plan.label}`}
          checked={!!draft.clubSiteIncluded}
          onChange={() => set({ clubSiteIncluded: !draft.clubSiteIncluded })} />
        <span>Club website</span>
      </label>
      <p className="poster-note" style={{ marginTop: 'var(--gs-space-2)' }}>
        A public page at greensheetbowls.online/&lt;club&gt;, which the club writes itself.
      </p>

      <div className="plan-actions">
        <button className="w3-button gs-btn-primary" disabled={!dirty}
          onClick={async () => { await onSave(plan, draft); setDirty(false); }}>
          {dirty ? 'Save changes' : 'Saved'}
        </button>
        <button className="w3-button w3-border" onClick={() => onDelete(plan)}
          disabled={plan.clubCount > 0}
          title={plan.clubCount > 0 ? 'Move its clubs to another plan first' : 'Delete this plan'}>
          <i className="fa fa-trash w3-margin-right"></i>Delete
        </button>
        {plan.clubCount > 0 && (
          <span className="w3-text-grey plan-note">
            In use — move its clubs before it can go.
          </span>
        )}
      </div>
    </div>
  );
}

function AdminPlans({ showToast, onPlansChanged }) {
  const [plans, setPlans] = React.useState(null);
  const [adding, setAdding] = React.useState(false);
  const [fresh, setFresh] = React.useState(BLANK);

  const load = React.useCallback(async () => {
    const r = await fetch(`${API}/api/subscriptions/plans`);
    if (r.ok) setPlans(await r.json());
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const announce = () => { load(); if (onPlansChanged) onPlansChanged(); };

  const save = async (plan, draft) => {
    const r = await fetch(`${API}/api/subscriptions/plans/${plan.id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(draft),
    });
    if (!r.ok) return showToast('That plan did not save', 'red');
    showToast(`${draft.label} saved`, 'green');
    return announce();
  };

  const remove = async plan => {
    const r = await fetch(`${API}/api/subscriptions/plans/${plan.id}`, { method: 'DELETE' });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) return showToast(data.error || 'It could not be deleted', 'red');
    showToast(`${plan.label} deleted`, 'green');
    return announce();
  };

  const create = async () => {
    const r = await fetch(`${API}/api/subscriptions/plans`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(fresh),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) return showToast(data.error || 'That plan could not be made', 'red');
    setFresh(BLANK); setAdding(false);
    showToast(`${data.label} added`, 'green');
    return announce();
  };

  return (
    <div className="gs-panel">
      <Section label="Plans"
        action={
          <button className="w3-button w3-small w3-border" onClick={() => setAdding(!adding)}>
            <i className="fa fa-plus w3-margin-right"></i>New plan
          </button>
        }>
        <p className="w3-text-grey plan-intro">
          What each level costs and what it entitles a club to. A club follows its plan
          unless a number has been set for it individually. Leave a limit empty for
          unlimited — zero means none.
        </p>

        {adding && (
          <div className="plan-row is-new">
            <div className="plan-head">
              <input className="w3-input plan-label" aria-label="New plan name"
                placeholder="Plan name" autoFocus
                value={fresh.label} onChange={e => setFresh({ ...fresh, label: e.target.value })} />
              <div className="plan-price">
                <span className="plan-currency">$</span>
                <input className="w3-input" type="number" min="0" step="0.01"
                  aria-label="New plan price" placeholder="0"
                  value={toDollars(fresh.priceCents)}
                  onChange={e => setFresh({ ...fresh, priceCents: toCents(e.target.value) })} />
                <span className="plan-per">/month</span>
              </div>
            </div>
            <div className="plan-actions">
              <button className="w3-button gs-btn-primary" onClick={create}
                disabled={!fresh.label.trim()}>Add plan</button>
              <button className="w3-button w3-border" onClick={() => setAdding(false)}>Cancel</button>
              <span className="w3-text-grey plan-note">
                Its limits can be set once it exists.
              </span>
            </div>
          </div>
        )}

        {!plans ? <p className="w3-text-grey">Loading…</p> : (
          <div className="plan-list">
            {plans.map(p => (
              <PlanRow key={p.id} plan={p} onSave={save} onDelete={remove} showToast={showToast} />
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

export default AdminPlans;
